import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import type {ReactNode} from "react";
import type {AuthState, JwtPayload, LoginResponse} from "../types";
import {API_BASE, onSessionRejected} from "../api";
import {isTokenExpired, parseJwt} from "../utils/jwt";
import {resolveAppRole} from "./roles";
import {
  AuthScope,
  clearStoredAuthTokens,
  isRoleAllowedForAuthScope,
  readStoredAuthTokens,
  writeStoredAuthTokens,
} from "./scope";

const AuthContext = createContext<AuthState | null>(null);

/** Пароль верен, но у аккаунта включён код из приложения: вход надо повторить с кодом. */
export class SecondFactorRequiredError extends Error {
  constructor() {
    super("Введите шесть цифр 2FA из приложения");
    this.name = "SecondFactorRequiredError";
  }
}

async function isSecondFactorRequired(res: Response): Promise<boolean> {
  if (res.status !== 403) return false;
  try {
    const body = (await res.json()) as {code?: string};
    return body.code === "second_factor_required";
  } catch {
    return false;
  }
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}

/** Try to restore a valid access token from sessionStorage */
function restoreSession(
  scope: AuthScope,
): {user: JwtPayload; token: string; refreshToken: string | null} | null {
  const stored = readStoredAuthTokens(scope);
  if (!stored.accessToken) return null;

  try {
    const payload = validateTokenForScope(stored.accessToken, scope);
    return {
      user: payload,
      token: stored.accessToken,
      refreshToken: stored.refreshToken,
    };
  } catch {
    clearStoredAuthTokens(scope);
    return null;
  }
}

function validateTokenForScope(token: string, scope: AuthScope): JwtPayload {
  const payload = parseJwt(token);
  if (isTokenExpired(payload)) {
    throw new Error("Received an already-expired token");
  }
  const role = resolveAppRole(payload.roles);
  if (!role) {
    throw new Error("Account does not have access to this application");
  }
  if (!isRoleAllowedForAuthScope(scope, role)) {
    throw new Error(
      scope === "merchant"
        ? "Вход на этой странице доступен только мерчанту"
        : "Для входа мерчанта откройте /#/merchant",
    );
  }
  return payload;
}

interface AuthProviderProps {
  children: ReactNode;
  scope: AuthScope;
}

export function AuthProvider({children, scope}: AuthProviderProps) {
  const [user, setUser] = useState<JwtPayload | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [refreshToken, setRefreshToken] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [sessionEnded, setSessionEnded] = useState(false);

  const clearSession = useCallback((): void => {
    setUser(null);
    setToken(null);
    setRefreshToken(null);
    clearStoredAuthTokens(scope);
  }, [scope]);

  const applySession = useCallback((data: LoginResponse): JwtPayload => {
    const payload = validateTokenForScope(data.jwt, scope);
    setUser(payload);
    setToken(data.jwt);
    setRefreshToken(data.refreshToken ?? null);
    writeStoredAuthTokens(scope, data.jwt, data.refreshToken ?? null);
    setSessionEnded(false);
    return payload;
  }, [scope]);

  // Ответ на запрос со старым токеном может прийти уже после нового входа: выходим, только если отклонён текущий.
  useEffect(() => {
    if (!token) return;
    return onSessionRejected((rejected) => {
      if (rejected !== token) return;
      clearSession();
      setSessionEnded(true);
    });
  }, [clearSession, token]);

  // Restore session on mount
  useEffect(() => {
    let cancelled = false;
    const restore = async (): Promise<void> => {
      const session = restoreSession(scope);
      if (session) {
        setUser(session.user);
        setToken(session.token);
        setRefreshToken(session.refreshToken);
        setLoading(false);
        return;
      }
      const storedRefreshToken = readStoredAuthTokens(scope).refreshToken;
      if (storedRefreshToken) {
        try {
          const refreshed = await refreshWithToken(storedRefreshToken);
          if (!cancelled) {
            applySession(refreshed);
          }
        } catch {
          clearSession();
        }
      }
      if (!cancelled) {
        setLoading(false);
      }
    };
    void restore();
    return () => {
      cancelled = true;
    };
  }, [applySession, clearSession, scope]);

  const refreshAccessToken = useCallback(async (): Promise<void> => {
    if (!refreshToken) {
      clearSession();
      return;
    }
    try {
      const data = await refreshWithToken(refreshToken);
      applySession(data);
    } catch {
      clearSession();
    }
  }, [applySession, clearSession, refreshToken]);

  // Refresh access shortly before expiry.
  useEffect(() => {
    if (!token) return;

    try {
      const payload = parseJwt(token);
      const nowSeconds = Math.floor(Date.now() / 1000);
      const msUntilRefresh = Math.max((payload.exp - nowSeconds - 60) * 1000, 0);

      const timer = setTimeout(() => {
        void refreshAccessToken();
      }, msUntilRefresh);
      return () => clearTimeout(timer);
    } catch {
      clearSession();
    }
  }, [clearSession, refreshAccessToken, token]);

  const login = async (loginStr: string, password: string, otp?: string): Promise<JwtPayload> => {
    const code = otp?.replace(/\s+/g, "");
    const res = await fetch(`${API_BASE}/auth/sign-in`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(code ? {login: loginStr, password, otp: code} : {login: loginStr, password}),
    });
    if (await isSecondFactorRequired(res)) throw new SecondFactorRequiredError();
    if (!res.ok) throw new Error(code ? "Неверные или уже использованные цифры 2FA" : "Invalid credentials");

    const data: LoginResponse = await res.json();
    try {
      return applySession(data);
    } catch (err) {
      clearSession();
      void revokeAccessToken(data.jwt);
      throw err;
    }
  };

  const logout = useCallback((): void => {
    const currentToken = token;
    clearSession();
    if (currentToken) {
      void revokeAccessToken(currentToken);
    }
  }, [clearSession, token]);

  if (loading) return null;

  return (
      <AuthContext.Provider value={{ user, token, sessionEnded, login, logout }}>
        {children}
      </AuthContext.Provider>
  );
}

async function refreshWithToken(refreshToken: string): Promise<LoginResponse> {
  const res = await fetch(`${API_BASE}/auth/refresh`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ refreshToken }),
  });
  if (!res.ok) throw new Error("Session refresh failed");
  return res.json() as Promise<LoginResponse>;
}

async function revokeAccessToken(token: string): Promise<void> {
  await fetch(`${API_BASE}/auth/logout`, {
    method: "POST",
    headers: {Authorization: `Bearer ${token}`},
  }).then(() => undefined).catch(() => undefined);
}
